import React, { useState } from "react";
import { useForm, useFieldArray, Controller, Control } from "react-hook-form";
import { ShoppingCart, Plus, Eye, Search, Trash2, X } from "lucide-react";
import {
  useOrders,
  useUpdateOrderStatus,
  useCancelOrder,
  usePlaceOrder,
  ListOrdersParams,
} from "../api/orders";
import { useProducts } from "../api/products";
import { useClients } from "../api/clients";
import { useAuth } from "../context/AuthContext";
import { Order, OrderStatus, Role } from "../types/models";
import { PageHeader } from "../components/ui/PageHeader";
import { Pagination } from "../components/ui/Pagination";
import { StatusBadge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { Button, SelectField, TextareaField } from "../components/ui/Form";
import { SearchableSelect } from "../components/ui/SearchableSelect";
import { LoadingState, ErrorState, EmptyState } from "../components/ui/States";
import { toast } from "../components/ui/Toast";
import { getErrorMessage } from "../lib/errors";
import { formatCurrency, formatDateTime, formatNumber, humanizeEnum, toNumber } from "../utils/helpers";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

const STAFF_ROLES: Role[] = [
  Role.ADMIN,
  Role.MANAGER,
  Role.STORE_KEEPER,
  Role.PRODUCTION_SUPERVISOR,
];

const TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  PENDING: [OrderStatus.CONFIRMED, OrderStatus.CANCELLED],
  CONFIRMED: [OrderStatus.PACKED, OrderStatus.CANCELLED],
  PACKED: [OrderStatus.DISPATCHED, OrderStatus.CANCELLED],
  DISPATCHED: [OrderStatus.DELIVERED],
  DELIVERED: [],
  CANCELLED: [],
};

export const Orders: React.FC = () => {
  const { isClient, isManagement, hasRole } = useAuth();
  const isStaff = hasRole(...STAFF_ROLES);

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<OrderStatus | "">("");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, 350);
  const [detail, setDetail] = useState<Order | null>(null);
  const [placing, setPlacing] = useState(false);

  const params: ListOrdersParams = {
    page,
    limit: 10,
    status: status || undefined,
    search: search || undefined,
  };
  const { data, isLoading, isError, error, refetch, isFetching } = useOrders(params);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Orders"
        subtitle="Track orders through the fulfilment workflow."
        actions={
          (isClient || isManagement) && (
            <Button icon={<Plus size={18} />} onClick={() => setPlacing(true)}>
              New Order
            </Button>
          )
        }
      />

      <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            placeholder="Search order number…"
            className="input pl-10"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as OrderStatus | "");
            setPage(1);
          }}
          className="input sm:w-52"
        >
          <option value="">All statuses</option>
          {Object.values(OrderStatus).map((s) => (
            <option key={s} value={s}>
              {humanizeEnum(s)}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={<ShoppingCart size={32} />}
            title="No orders yet"
            description={isClient ? "Place your first order to get started." : "Orders will appear here as clients place them."}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3">Order</th>
                    {!isClient && <th className="px-4 py-3">Client</th>}
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Items</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Placed</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className={isFetching ? "opacity-60 transition-opacity" : ""}>
                  {data?.items.map((o) => (
                    <tr key={o.id} className="border-b border-slate-800 last:border-0 hover:bg-slate-800/40">
                      <td className="px-4 py-3 font-medium text-slate-100">{o.orderNumber}</td>
                      {!isClient && (
                        <td className="px-4 py-3 text-slate-300">
                          {o.client?.companyName ?? "—"}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <StatusBadge status={o.status} />
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300">
                        {o.items?.length ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-200">
                        {formatCurrency(o.totalAmount)}
                      </td>
                      <td className="px-4 py-3 text-slate-400">{formatDateTime(o.placedAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <button
                            onClick={() => setDetail(o)}
                            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-slate-300 hover:bg-slate-700 hover:text-slate-100"
                          >
                            <Eye size={16} /> View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={data?.meta} onPageChange={setPage} />
          </>
        )}
      </div>

      {detail && (
        <OrderDetailModal
          order={detail}
          canManageStatus={isStaff}
          canCancel={isManagement || (isClient && detail.status === OrderStatus.PENDING)}
          onClose={() => setDetail(null)}
        />
      )}
      {placing && <PlaceOrderModal isManagement={isManagement} onClose={() => setPlacing(false)} />}
    </div>
  );
};

// ───────────────────────── Order detail ─────────────────────────

const OrderDetailModal: React.FC<{
  order: Order;
  canManageStatus: boolean;
  canCancel: boolean;
  onClose: () => void;
}> = ({ order, canManageStatus, canCancel, onClose }) => {
  const updateStatus = useUpdateOrderStatus();
  const cancel = useCancelOrder();
  const nextStatuses = TRANSITIONS[order.status].filter((s) => s !== OrderStatus.CANCELLED);
  const busy = updateStatus.isPending || cancel.isPending;

  const advance = async (status: OrderStatus) => {
    try {
      await updateStatus.mutateAsync({ id: order.id, status });
      toast.success(`Order ${humanizeEnum(status).toLowerCase()}`);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const doCancel = async () => {
    try {
      await cancel.mutateAsync(order.id);
      toast.success("Order cancelled");
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Order ${order.orderNumber}`}
      description={order.client?.companyName ?? undefined}
      footer={
        <div className="flex w-full items-center justify-between">
          <div>
            {canCancel && order.status !== OrderStatus.CANCELLED && order.status !== OrderStatus.DELIVERED && (
              <Button variant="danger" icon={<X size={16} />} onClick={doCancel} loading={cancel.isPending}>
                Cancel Order
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {canManageStatus &&
              nextStatuses.map((s) => (
                <Button key={s} onClick={() => advance(s)} loading={busy}>
                  Mark {humanizeEnum(s)}
                </Button>
              ))}
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <StatusBadge status={order.status} />
          <span className="text-sm text-slate-400">
            Placed {formatDateTime(order.placedAt)}
          </span>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-900/40 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Unit Price</th>
                <th className="px-3 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((it) => (
                <tr key={it.id} className="border-b border-slate-800 last:border-0">
                  <td className="px-3 py-2 text-slate-200">{it.productName}</td>
                  <td className="px-3 py-2 text-right text-slate-300">{formatNumber(it.quantity, 3)}</td>
                  <td className="px-3 py-2 text-right text-slate-300">{formatCurrency(it.unitPrice)}</td>
                  <td className="px-3 py-2 text-right text-slate-200">{formatCurrency(it.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
          <Row label="Subtotal" value={formatCurrency(order.subtotal)} />
          <Row label="Tax" value={formatCurrency(order.taxAmount)} />
          <div className="border-t border-slate-700 pt-1">
            <Row label="Total" value={formatCurrency(order.totalAmount)} bold />
          </div>
        </div>

        {order.notes && (
          <p className="rounded-lg bg-slate-900/40 p-3 text-sm text-slate-300">
            <span className="font-medium text-slate-200">Notes: </span>
            {order.notes}
          </p>
        )}
      </div>
    </Modal>
  );
};

const Row: React.FC<{ label: string; value: string; bold?: boolean }> = ({ label, value, bold }) => (
  <div className="flex justify-between">
    <span className="text-slate-400">{label}</span>
    <span className={bold ? "font-semibold text-slate-100" : "text-slate-200"}>{value}</span>
  </div>
);

// ───────────────────────── Place order ─────────────────────────

interface PlaceOrderForm {
  clientId?: string;
  notes?: string;
  items: { productId: string; quantity: number }[];
}

export const PlaceOrderModal: React.FC<{
  isManagement: boolean;
  /** When set, the order is locked to this client (no client picker shown). */
  lockedClient?: { id: string; companyName: string };
  onClose: () => void;
}> = ({ isManagement, lockedClient, onClose }) => {
  const products = useProducts({ limit: 100, isActive: true });
  const place = usePlaceOrder();
  const { register, control, handleSubmit, watch } = useForm<PlaceOrderForm>({
    defaultValues: { clientId: lockedClient?.id ?? "", items: [{ productId: "", quantity: 1 }] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: "items" });
  const watched = watch("items");
  const catalogue = products.data?.items ?? [];

  const estTotal = watched.reduce((sum, line) => {
    const p = catalogue.find((c) => c.id === line.productId);
    return sum + toNumber(p?.price) * (Number(line.quantity) || 0);
  }, 0);

  const onSubmit = handleSubmit(async (values) => {
    const clientId = lockedClient?.id ?? values.clientId;
    if (isManagement && !clientId) {
      toast.error("Select a client for this order");
      return;
    }
    const items = values.items
      .filter((i) => i.productId && Number(i.quantity) > 0)
      .map((i) => ({ productId: i.productId, quantity: Number(i.quantity) }));
    if (items.length === 0) {
      toast.error("Add at least one product");
      return;
    }
    try {
      const order = await place.mutateAsync({
        items,
        notes: values.notes || undefined,
        clientId: isManagement ? clientId : undefined,
      });
      toast.success(`Order ${order.orderNumber} placed`);
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={lockedClient ? `New Order — ${lockedClient.companyName}` : isManagement ? "Create Order for Client" : "Place New Order"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} loading={place.isPending}>
            Place order ({formatCurrency(estTotal)})
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {lockedClient ? (
          <div className="rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2 text-sm">
            <span className="text-slate-400">Client: </span>
            <span className="font-medium text-slate-100">{lockedClient.companyName}</span>
          </div>
        ) : isManagement ? (
          <ClientPicker control={control} />
        ) : null}
        <div className="space-y-3">
          {fields.map((field, idx) => (
            <div key={field.id} className="flex items-end gap-2">
              <SelectField label={idx === 0 ? "Product" : undefined} className="flex-1" {...register(`items.${idx}.productId`)}>
                <option value="">Select…</option>
                {catalogue.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {formatCurrency(p.price)}
                  </option>
                ))}
              </SelectField>
              <div className="w-28">
                <label className={idx === 0 ? "mb-1.5 block text-sm font-medium text-slate-300" : "hidden"}>
                  Qty
                </label>
                <input
                  type="number"
                  min={1}
                  step="0.001"
                  className="input"
                  {...register(`items.${idx}.quantity`)}
                />
              </div>
              <button
                type="button"
                onClick={() => fields.length > 1 && remove(idx)}
                className="mb-1 rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-red-400"
                aria-label="Remove line"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <Button variant="secondary" type="button" icon={<Plus size={16} />} onClick={() => append({ productId: "", quantity: 1 })}>
          Add item
        </Button>
        <TextareaField label="Notes" placeholder="Delivery instructions, etc. (optional)" {...register("notes")} />
      </div>
    </Modal>
  );
};

/**
 * Client selector for management-created orders. Kept as its own component so
 * the `/clients` fetch only runs when management opens the modal (clients never
 * mount it, so they never hit the management-only endpoint).
 */
const ClientPicker: React.FC<{ control: Control<PlaceOrderForm> }> = ({ control }) => {
  const clients = useClients({ limit: 100 });
  const options = (clients.data?.items ?? []).map((c) => ({
    value: c.id,
    label: c.user?.fullName ? `${c.companyName} — ${c.user.fullName}` : c.companyName,
  }));
  return (
    <Controller
      control={control}
      name="clientId"
      render={({ field }) => (
        <SearchableSelect
          label="Client"
          required
          value={field.value ?? ""}
          onChange={field.onChange}
          options={options}
          placeholder={clients.isLoading ? "Loading clients…" : "Select client…"}
        />
      )}
    />
  );
};
